import React, { useState, useEffect, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { THEME, shadows } from '../constants/theme';
import { supabase } from '../lib/supabase';
import { formatDistanceToNow } from 'date-fns';
import { useAnalytics } from '../hooks/useAnalytics';

// Toast notification component
const Toast = ({ notification, onClose }) => (
  <div 
    className="fixed bottom-4 right-4 w-96 bg-white rounded-lg shadow-lg transform transition-all duration-500 ease-in-out z-50"
    style={{ boxShadow: shadows.lg }}
  >
    <div className="p-4">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="text-sm font-medium" style={{ color: THEME.text }}>
            {notification.title}
          </h3>
          <p className="mt-1 text-sm" style={{ color: THEME.subtext }}>
            {notification.message}
          </p>
        </div>
        <button
          onClick={onClose}
          className="ml-4 text-gray-400 hover:text-gray-500"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
    <div 
      className="h-1 bg-primary rounded-b-lg animate-shrink"
      style={{ backgroundColor: THEME.primary }}
    />
  </div>
);

export default function NotificationsDropdown({ user }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toast, setToast] = useState(null);
  const { logEvent } = useAnalytics();

  useEffect(() => {
    if (user) {
      fetchNotifications();
      const subscription = subscribeToNotifications();
      return () => {
        subscription?.unsubscribe();
      };
    }
  }, [user]);


  const fetchNotifications = async () => {
    try {
      const { data: notificationsData, error: notificationsError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      logEvent('Notifications', 'Fetch Notifications', `User ID: ${user.id}`);

      if (notificationsError) throw notificationsError;
      logEvent('Notifications', 'Fetch Notifications Success', `User ID: ${user.id}`);

      // Fetch quote numbers for these notifications
      const quoteIds = notificationsData
        .map(n => n.data?.quote_id)
        .filter(id => id);

      const { data: quotesData } = await supabase
        .from('quotes')
        .select('quote_id, quote_number')
        .in('quote_id', quoteIds);

      // Create a map of quote_id to quote_number
      const quoteNumberMap = quotesData?.reduce((acc, quote) => ({
        ...acc,
        [quote.quote_id]: quote.quote_number
      }), {});

      // Combine the data
      const enrichedNotifications = notificationsData.map(notification => ({
        ...notification,
        quote_number: quoteNumberMap[notification.data?.quote_id]
      }));

      setNotifications(enrichedNotifications || []);
      setUnreadCount(enrichedNotifications?.filter(n => !n.read).length || 0);
    } catch (error) {
      logEvent('Notifications', 'Fetch Notifications Error', error.message);
    }
  };

  const subscribeToNotifications = () => {
    return supabase
      .channel('notifications-channel')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          
          // Fetch the quote number if this notification is related to a quote
          let quoteNumber = null;
          if (payload.new.data?.quote_id) {
            const { data: quoteData } = await supabase
              .from('quotes')
              .select('quote_number')
              .eq('quote_id', payload.new.data.quote_id)
              .single();
            quoteNumber = quoteData?.quote_number;
          }

          const newNotification = {
            ...payload.new,
            quote_number: quoteNumber
          };

          setNotifications(prev => [newNotification, ...prev].slice(0, 10));
          setUnreadCount(prev => prev + 1);
          
          // Show toast notification
          setToast(newNotification);
          // Auto-dismiss after 5 seconds
          setTimeout(() => setToast(null), 5000);
        }
      )
      .subscribe();
  };

  const getNotificationContent = (notification) => {
    const quoteNumber = notification.quote_number;
    
    switch (notification.type) {
      case 'comment':
        return {
          title: `New comment on Quote ${quoteNumber || ''}`,
          message: notification.data?.comment || 'Someone commented on your quote'
        };
      case 'quote_accepted':
        return {
          title: `Quote ${quoteNumber || ''} Accepted`,
          message: `The client has accepted your quote`
        };
      case 'quote_declined':
        return {
          title: `Quote ${quoteNumber || ''} Declined`,
          message: `The client has declined your quote`
        };
      default:
        return {
          title: notification.title || 'New Notification',
          message: notification.message || notification.data?.message || ''
        };
    }
  };

  const handleNotificationClick = async (notification) => {
    logEvent('Notifications', 'Click Notification', `Type: ${notification.type}`);
    try {
      // Mark as read
      if (!notification.read) {
        const { error } = await supabase
          .from('notifications')
          .update({ read: true })
          .eq('id', notification.id);

        if (error) throw error;

        setNotifications(prev =>
          prev.map(n =>
            n.id === notification.id ? { ...n, read: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      // Navigate based on notification type
      switch (notification.type) {
        case 'comment':
          navigate(`/quotes/${notification.data.quote_id}?scrollToComments=true`);
          break;
        case 'quote_accepted':
        case 'quote_declined':
          navigate(`/quotes/${notification.data.quote_id}`);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

  return (
    <>
      <Menu as="div" className="relative ml-3">
        <Menu.Button className="relative flex items-center p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
          <BellIcon className="h-6 w-6 text-gray-400" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-xs text-white"
              style={{ backgroundColor: THEME.primary }}>
              {unreadCount}
            </span>
          )}
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 mt-2 w-96 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="px-4 py-2 border-b border-gray-100">
              <h3 className="text-sm font-medium" style={{ color: THEME.text }}>Notifications</h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm" style={{ color: THEME.subtext }}>
                  No notifications yet
                </div>
              ) : (
                notifications.map((notification) => {
                  const content = getNotificationContent(notification);
                  return (
                    <Menu.Item key={notification.id}>
                      {({ active }) => (
                        <button
                          onClick={() => handleNotificationClick(notification)}
                          className={`${
                            active ? 'bg-gray-50' : ''
                          } w-full px-4 py-3 text-left ${!notification.read ? 'bg-blue-50' : ''}`}
                        >
                          <div className="flex justify-between">
                            <p className="text-sm font-medium" style={{ color: THEME.text }}>
                              {content.title}
                            </p>
                            <span className="text-xs" style={{ color: THEME.subtext }}>
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="text-sm mt-1" style={{ color: THEME.subtext }}>
                            {content.message}
                          </p>
                        </button>
                      )}
                    </Menu.Item>
                  );
                })
              )}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      {/* Toast Notification */}
      {toast && (
        <Toast 
          notification={getNotificationContent(toast)}
          onClose={() => setToast(null)}
        />
      )}

      {/* Add styles for toast animation */}
      <style jsx global>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        .animate-shrink {
          animation: shrink 5s linear forwards;
        }
      `}</style>
    </>
  );
} 